import _ from 'lodash';

import * as api from '../resources/manage-icons.api';

const FETCH_ICONS_REQUEST = 'FETCH_ICONS_REQUEST';
const FETCH_ICONS_SUCCESS = 'FETCH_ICONS_SUCCESS';
const FETCH_ICONS_FAILURE = 'FETCH_ICONS_FAILURE';

export const fetchIconsRequest = () => ({
  type: FETCH_ICONS_REQUEST,
  isLoading: true,
});

export const fetchIconsSuccess = ({ entries, versionType }) => ({
  type: FETCH_ICONS_SUCCESS,
  isLoading: false,
  entries,
  versionType,
});

export const fetchIconsFailure = () => ({
  type: FETCH_ICONS_FAILURE,
  isLoading: false,
});

export const fetchIcons = (approved = false) => (dispatch, getState) => {
  const state = getState();
  const { jurisdiction } = state.session;
  const { notification, error } = state.manageIcons;

  const config = {
    jurisdiction,
    approved,
  };

  dispatch(fetchIconsRequest());

  if (notification !== '' && error !== '') dispatch(clearNotifications());

  return api
    .fetchIcons(config)
    .then(({ entries, versionType }) => dispatch(fetchIconsSuccess({ entries, versionType })))
    .catch(() => dispatch(fetchIconsFailure()));
};

const UPLOAD_ICON_REQUEST = 'UPLOAD_ICON_REQUEST';
const UPLOAD_ICON_SUCCESS = 'UPLOAD_ICON_SUCCESS';
const UPLOAD_ICON_FAILURE = 'UPLOAD_ICON_FAILURE';

export const uploadIconRequest = () => ({
  type: UPLOAD_ICON_REQUEST,
  isFileUploading: true,
});

export const uploadIconSuccess = (notification, iconsApprovalStatus) => ({
  type: UPLOAD_ICON_SUCCESS,
  isFileUploading: false,
  notification,
  iconsApprovalStatus,
});

const uploadIconSuccessAsnc = (notification, iconsApprovalStatus) => (dispatch) => new Promise((resolve) => {
  resolve(dispatch(uploadIconSuccess(notification, iconsApprovalStatus)));
});

export const uploadIconFailure = () => ({
  type: UPLOAD_ICON_FAILURE,
  isFileUploading: false,
});

export const uploadIcon = () => (dispatch, getState) => {
  const state = getState();
  const { jurisdiction, privileges } = state.session;
  const { secondHandApproval } = state.config;
  const { entries } = state.manageIcons;
  const { approve } = privileges.cms.widgetsManagement;
  const canApprove = approve || !secondHandApproval;

  const config = {
    jurisdiction,
    entries,
  };

  dispatch(uploadIconRequest());

  return api
    .uploadIcon(config)
    .then(() => {
      const notification = canApprove ? api.codes.ICON_PUBLISH_SUCCESS : api.codes.ICON_UPDATE_SUCCESS;
      const iconsApprovalStatus = canApprove ? 'APPROVED' : 'DRAFT';

      return dispatch(uploadIconSuccessAsnc(notification, iconsApprovalStatus)).then(() => dispatch(clearNotifications()));
    })
    .catch(() => {
      dispatch(uploadIconFailure());
      dispatch(cancelIconsChanges());
    });
};

const SELECT_ICON_REQUEST = 'SELECT_ICON_REQUEST';
const SELECT_ICON_SUCCESS = 'SELECT_ICON_SUCCESS';
const SELECT_ICON_FAILURE = 'SELECT_ICON_FAILURE';

export const selectIconRequest = () => ({
  type: SELECT_ICON_REQUEST,
  startFileSelection: true,
});

export const selectIconSuccess = (icon) => ({
  type: SELECT_ICON_SUCCESS,
  startFileSelection: false,
  notification: api.codes.ICON_UPLOAD_SUCCESS,
  icon,
});

export const selectIconFailure = (error) => ({
  type: SELECT_ICON_FAILURE,
  startFileSelection: false,
  error,
});

export const selectIcon = (file, options = {}) => (dispatch) => {
  dispatch(selectIconRequest());
  dispatch(clearNotifications());

  return fileReader(file, options)
    .then((icon) => validateImageRatio(icon))
    .then((icon) => {
      // The file's result results in a string that cannot be directly decoded as Base64
      // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
      const base64Format = icon.source.replace(`data:${icon.type};base64,`, '');

      return {
        title: icon.name,
        created: Date.now(),
        fields: {
          en_US: {
            thumbnail: icon.source,
          },
        },
        attachments: {
          en_US: [
            {
              name: icon.name,
              mimeType: icon.type,
              content: base64Format,
            },
          ],
        },
      };
    })
    .then((icon) => dispatch(selectIconSuccess(icon)))
    .catch((error) => {
      const isSupportedError = api.errors.find((er) => er.code === error);
      if (isSupportedError) {
        dispatch(selectIconFailure(error));
      }
    });
};

const DISCARD_CHANGES_REQUEST = 'DISCARD_CHANGES_REQUEST';
const DISCARD_CHANGES_SUCCESS = 'DISCARD_CHANGES_SUCCESS';
const DISCARD_CHANGES_FAILURE = 'DISCARD_CHANGES_FAILURE';

export const discardIconsRequest = () => ({
  type: DISCARD_CHANGES_REQUEST,
  isLoading: true,
});

export const discardIconsSuccess = () => ({
  type: DISCARD_CHANGES_SUCCESS,
  isLoading: false,
  notification: api.codes.ICON_DISCARD_SUCCESS,
});

export const discardIconsFailure = () => ({
  type: DISCARD_CHANGES_FAILURE,
  isLoading: false,
});

export const discardIcons = () => (dispatch, getState) => {
  const state = getState();
  const { jurisdiction } = state.session;

  const config = {
    jurisdiction,
  };

  dispatch(discardIconsRequest());

  return api
    .discardIcons(config)
    .then(() => {
      dispatch(fetchIcons());
      return dispatch(discardIconsSuccess());
    })
    .catch(() => dispatch(discardIconsFailure()));
};

const DELETE_ICON = 'DELETE_ICON';

export const deleteIconsSuccess = (icon) => ({
  type: DELETE_ICON,
  notification: api.codes.ICON_DELETE_SUCCESS,
  icon,
});

const deleteIconAsnc = (icon) => (dispatch) => new Promise((resolve) => {
  resolve(dispatch(deleteIconsSuccess(icon)));
});

export const deleteIcon = (icon) => (dispatch) => {
  dispatch(deleteIconAsnc(icon)).then(() => dispatch(clearNotifications()));
};

const CANCEL_CHANGES = 'CANCEL_CHANGES';

export const cancelIconsChanges = () => ({
  type: CANCEL_CHANGES,
});

const CLEAR_NOTIFICATIONS = 'CLEAR_NOTIFICATIONS';

export const clearNotifications = () => ({
  type: CLEAR_NOTIFICATIONS,
  notification: '',
  error: '',
});

export default {
  FETCH_ICONS_REQUEST,
  FETCH_ICONS_SUCCESS,
  FETCH_ICONS_FAILURE,
  UPLOAD_ICON_REQUEST,
  UPLOAD_ICON_FAILURE,
  UPLOAD_ICON_SUCCESS,
  SELECT_ICON_REQUEST,
  SELECT_ICON_FAILURE,
  SELECT_ICON_SUCCESS,
  DISCARD_CHANGES_REQUEST,
  DISCARD_CHANGES_SUCCESS,
  DISCARD_CHANGES_FAILURE,
  DELETE_ICON,
  CANCEL_CHANGES,
  CLEAR_NOTIFICATIONS,
};

const fileReader = (file, options = {}) => {
  const fileReader = new FileReader();

  return new Promise((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort();
      reject(api.codes.FILE_ERROR);
    };

    fileReader.onload = () => {
      const MAX_FILE_SIZE = 300; // KB
      const size = Math.round(file.size / 1024);
      const resolveObj = {
        name: file.name,
        type: file.type,
        source: fileReader.result,
      };
      const ext = (`.${_.last(file.name.split('.'))}`).toLowerCase();

      if (size > MAX_FILE_SIZE) reject(api.codes.FILE_SIZE_EXCEEDED);

      if (_.isArray(options.accept) && !options.accept.includes(ext)) reject(api.codes.FILE_EXTENSION_ERROR);

      resolve(resolveObj);
    };

    fileReader.readAsDataURL(file);
  });
};

const validateImageRatio = (file) => {
  const img = new Image();

  return new Promise((resolve, reject) => {
    img.src = file.source;

    img.onload = () => {
      if (img.height > img.width) reject(api.codes.FILE_RATIO_ERROR);

      resolve(file);
    };

    img.onerror = () => {
      reject(api.codes.FILE_ERROR);
    };
  });
};



// WEBPACK FOOTER //
// ./src/modules/app/actions/manage-icons.actions.js